import { FC } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
  CircularProgress,
} from '@mui/material';

import { ID } from 'domain/types/ID';
import { useCaseCancelCustomerJobRequest } from 'application/jobs/useCases/useCaseCancelCustomerJobRequest';
import { useTranslationPrefix } from 'infrastructure/translations/i18n';
import useSnackbar from 'targets/web/modules/dashboard/hooks/useSnackbar';

interface CancelJobRequestConfirmationProps {
  open: boolean;
  onClose: () => void;
  jobRequestId?: ID;
  onSuccess?: () => void;
}

export const CancelJobRequestConfirmation: FC<CancelJobRequestConfirmationProps> = ({
  open,
  onClose,
  jobRequestId,
  onSuccess,
}) => {
  const t = useTranslationPrefix('customer_portal.job_cancel_confirmation');
  const enqueueSnackbar = useSnackbar();

  const { cancelCustomerJobRequest, isLoading } = useCaseCancelCustomerJobRequest();

  const handleCancel = async () => {
    if (!jobRequestId) {
      return;
    }
    try {
      await cancelCustomerJobRequest({ jobRequestId });
      enqueueSnackbar(t('success_message'), { variant: 'success' });
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (e) {
      enqueueSnackbar(
        t('error', {
          message: (e as { body?: { message?: string } }).body?.message || 'unknown',
        }),
        {
          variant: 'error',
        },
      );
    }
  };

  return (
    <>
      <Dialog open={open} onClose={isLoading ? undefined : onClose} maxWidth="sm" fullWidth>
        <DialogTitle>{t('title')}</DialogTitle>
        <DialogContent>
          <Stack spacing={3}>
            <Typography variant="body1">{t('confirmation_message')}</Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isLoading} color="primary">
            {t('back')}
          </Button>
          <Button
            onClick={handleCancel}
            variant="contained"
            color="error"
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {t('confirm_cancel')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CancelJobRequestConfirmation;
